import { useState } from "react";
import { Plus, RotateCcw, Trash2, Undo2 } from "lucide-react";
import HelpTooltip from "@/components/shared/HelpTooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useI18n } from "@/lib/i18n";
import { planIndex } from "@/lib/plans";
import {
  DEFAULT_SUMMARY_CUSTOM_INSTRUCTION_LINES,
  SUMMARY_CUSTOM_CONTEXT_MAX_LENGTH,
  normalizeSummaryDictionaryEntries,
} from "@/lib/preferences";

const SUMMARY_DETAIL_OPTIONS = [
  {
    value: "main_points",
    label: "Main points",
    tooltip: "Example: Sarah is delayed and will send the new arrival time.",
  },
  {
    value: "complete",
    label: "Complete",
    tooltip: "Example: Sarah is delayed, will send the new arrival time, and hopes everyone is well.",
  },
  {
    value: "bullets",
    label: "Bullets",
    tooltip: "Example: - Sarah is delayed. - She will send the new arrival time. - She hopes everyone is well.",
  },
  {
    value: "flow",
    label: "Flow",
    tooltip: "Example: Sarah explains the delay, then the updated timing, then the personal check-in.",
  },
  {
    value: "custom",
    label: "Custom",
    tooltip: "Write your own summarization instructions.",
  },
];

const SUMMARY_CONTEXT_OPTIONS = [
  {
    value: "chat_context",
    label: "Use chat context",
    tooltip: "Uses recent chat only for spelling, names, and reference resolution.",
  },
  {
    value: "transcript_only",
    label: "Transcript only",
    tooltip: "Summarizes only the current voice note transcript plus your summary dictionary.",
  },
];

const VOICE_NOTE_DURATION_OPTIONS = [
  { value: "1200", label: "20 min", tooltip: "Default voice-note limit." },
  { value: "3600", label: "60 min", tooltip: "For longer voice notes on Pro." },
  { value: "7200", label: "120 min", tooltip: "For long briefings on Pro." },
];

function isPlanLocked(requiredPlan, effectivePlan) {
  return planIndex(requiredPlan) > planIndex(effectivePlan);
}

function getSummaryDictionaryContext(entries) {
  return normalizeSummaryDictionaryEntries(entries).join("\n");
}

function getUncappedSummaryDictionaryContext(entries) {
  return normalizeSummaryDictionaryEntries(entries, { maxChars: Number.POSITIVE_INFINITY }).join("\n");
}

function SegmentedPreference({ label, tooltip, value, options, onValueChange }) {
  const { tx } = useI18n();

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-foreground">{tx(label)}</span>
        <HelpTooltip content={tx(tooltip)} />
      </div>
      <ToggleGroup
        type="single"
        value={String(value)}
        onValueChange={(nextValue) => {
          if (nextValue) {
            onValueChange(nextValue);
          }
        }}
        className={`grid w-full grid-cols-1 rounded-xl border border-border bg-background p-1 ${options.length === 2 ? "sm:grid-cols-2" : options.length === 4 ? "sm:grid-cols-4" : options.length === 5 ? "sm:grid-cols-2 xl:grid-cols-5" : "sm:grid-cols-3"}`}
      >
        {options.map((option) => (
          <TooltipProvider key={option.value} delayDuration={150}>
            <Tooltip>
              <ToggleGroupItem
                value={option.value}
                aria-label={tx(option.label)}
                className="h-auto min-h-10 whitespace-normal rounded-lg px-3 py-2 text-xs leading-tight data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
              >
                <TooltipTrigger asChild>
                  <span className="flex min-h-6 w-full items-center justify-center">
                    {tx(option.label)}
                  </span>
                </TooltipTrigger>
              </ToggleGroupItem>
              <TooltipContent side="top" className="max-w-[260px] border border-border/80 bg-background px-3 py-2 text-[13px] text-foreground shadow-xl shadow-black/10">
                {tx(option.tooltip)}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ))}
      </ToggleGroup>
    </div>
  );
}

export default function SummaryPreferencesSection({
  preferences,
  setPreferences,
  effectivePlan,
  funPlanRequirementCopy,
  proPlanRequirementCopy,
}) {
  const { tx } = useI18n();
  const [customPromptResetDraft, setCustomPromptResetDraft] = useState(null);
  const dictionaryEntries = Array.isArray(preferences.summaryDictionaryEntries)
    ? preferences.summaryDictionaryEntries
    : [];
  const hasDictionaryEntries = dictionaryEntries.some((entry) => String(entry || "").trim())
    || Boolean(preferences.summaryCustomContext);
  const defaultCustomInstructions = DEFAULT_SUMMARY_CUSTOM_INSTRUCTION_LINES
    .map((line) => tx(line))
    .join("\n");
  const updateSummaryPreference = (key, value) => {
    setPreferences((current) => ({ ...current, [key]: value }));
  };
  const selectSummaryDetailMode = (value) => {
    setCustomPromptResetDraft(null);
    setPreferences((current) => ({
      ...current,
      summaryDetailMode: value,
      ...(value === "custom" && !current.summaryCustomInstructions
        ? { summaryCustomInstructions: defaultCustomInstructions }
        : {}),
    }));
  };
  const updateCustomInstructions = (value) => {
    setCustomPromptResetDraft(null);
    updateSummaryPreference("summaryCustomInstructions", value);
  };
  const updateDictionaryEntries = (entries) => {
    setPreferences((current) => {
      const uncappedSummaryCustomContext = getUncappedSummaryDictionaryContext(entries);
      if (uncappedSummaryCustomContext.length > SUMMARY_CUSTOM_CONTEXT_MAX_LENGTH) {
        return current;
      }
      const summaryCustomContext = getSummaryDictionaryContext(entries);
      return {
        ...current,
        summaryDictionaryEntries: entries,
        summaryCustomContext,
      };
    });
  };
  const updateDictionaryEntry = (index, value) => {
    updateDictionaryEntries(dictionaryEntries.map((entry, entryIndex) => (
      entryIndex === index ? value : entry
    )));
  };
  const addDictionaryEntry = () => {
    updateDictionaryEntries([...dictionaryEntries, ""]);
  };
  const removeDictionaryEntry = (index) => {
    updateDictionaryEntries(dictionaryEntries.filter((_, entryIndex) => entryIndex !== index));
  };
  const resetCustomPrompt = () => {
    setPreferences((current) => {
      setCustomPromptResetDraft({
        summaryDetailMode: current.summaryDetailMode,
        summaryCustomInstructions: current.summaryCustomInstructions || defaultCustomInstructions,
      });
      return {
        ...current,
        summaryDetailMode: "complete",
        summaryCustomInstructions: "",
      };
    });
  };
  const undoCustomPromptReset = () => {
    if (!customPromptResetDraft) {
      return;
    }
    setPreferences((current) => ({ ...current, ...customPromptResetDraft }));
    setCustomPromptResetDraft(null);
  };
  const customPromptHidden = preferences.summaryDetailMode !== "custom"
    && Boolean(preferences.summaryCustomInstructions);
  const summaryStyleLocked = isPlanLocked("fun", effectivePlan) && (
    preferences.summaryDetailMode !== "complete"
    || preferences.summaryContextMode !== "chat_context"
    || hasDictionaryEntries
    || Boolean(preferences.summaryCustomInstructions)
  );
  const longerVoiceNotesLocked = isPlanLocked("pro", effectivePlan)
    && Number(preferences.voiceNoteMaxDurationSec || 1200) > 1200;

  return (
    <section className="rounded-[1.75rem] border border-border bg-card p-5 md:col-span-2 md:p-6">
      <div className="flex items-center gap-2">
        <h2 className="font-display text-2xl font-bold">{tx("Summary output")}</h2>
        <HelpTooltip content={tx("Choose how Min turns each voice note into the final WhatsApp summary.")} />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-2 lg:items-start">
        <div className="space-y-5">
          <SegmentedPreference
            label="Summarisation style"
            tooltip="Choose how much of the voice note Min should preserve."
            value={preferences.summaryDetailMode}
            options={SUMMARY_DETAIL_OPTIONS}
            onValueChange={selectSummaryDetailMode}
          />
          {preferences.summaryDetailMode === "custom" ? (
            <div className="rounded-2xl border border-border bg-background/80 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <label htmlFor="summary-custom-instructions" className="text-sm font-semibold text-foreground">
                    {tx("Custom summarization instructions")}
                  </label>
                  <HelpTooltip content={tx("Edit the extra instructions Min uses when Detail is Custom.")} />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-9 rounded-xl px-3 text-muted-foreground"
                  onClick={resetCustomPrompt}
                >
                  <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
                  {tx("Reset to default")}
                </Button>
              </div>
              <Textarea
                id="summary-custom-instructions"
                value={preferences.summaryCustomInstructions || ""}
                onChange={(event) => updateCustomInstructions(event.target.value)}
                placeholder={defaultCustomInstructions}
                className="mt-3 min-h-[168px] rounded-2xl bg-card font-mono text-sm leading-6"
                maxLength={4000}
              />
            </div>
          ) : null}
          {customPromptResetDraft ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
              <span>{tx("Custom instructions reset.")}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 rounded-xl px-2 text-primary"
                onClick={undoCustomPromptReset}
              >
                <Undo2 className="mr-2 h-4 w-4" aria-hidden="true" />
                {tx("Undo reset")}
              </Button>
            </div>
          ) : customPromptHidden ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
              <span>{tx("Custom instructions saved.")}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 rounded-xl px-2"
                onClick={resetCustomPrompt}
              >
                <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
                {tx("Reset to default")}
              </Button>
            </div>
          ) : null}
        </div>

        <div className="space-y-5">
          <SegmentedPreference
            label="Context"
            tooltip="Choose whether recent chat can help resolve names and references."
            value={preferences.summaryContextMode}
            options={SUMMARY_CONTEXT_OPTIONS}
            onValueChange={(value) => updateSummaryPreference("summaryContextMode", value)}
          />
          <SegmentedPreference
            label="Voice note length"
            tooltip="Choose the longest voice note Min should summarise automatically."
            value={String(preferences.voiceNoteMaxDurationSec || 1200)}
            options={VOICE_NOTE_DURATION_OPTIONS}
            onValueChange={(value) => updateSummaryPreference("voiceNoteMaxDurationSec", Number(value))}
          />
        </div>
      </div>

      <div className="mt-5 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-foreground">
            {tx("Summary dictionary")}
          </span>
          <HelpTooltip content={tx("Words or phrases Min should recognize in summaries.")} />
        </div>
        <div className="space-y-2">
          {dictionaryEntries.map((entry, index) => (
            <div key={index} className="flex min-h-12 items-center gap-2 rounded-xl bg-background">
              <Input
                value={entry}
                onChange={(event) => updateDictionaryEntry(index, event.target.value)}
                aria-label={tx("Dictionary entry")}
                placeholder={tx("Add a word or phrase.")}
                className="h-12 rounded-xl bg-background text-sm"
                maxLength={SUMMARY_CUSTOM_CONTEXT_MAX_LENGTH}
              />
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-12 w-12 shrink-0 rounded-xl text-muted-foreground"
                      aria-label={tx("Remove entry")}
                      onClick={() => removeDictionaryEntry(index)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {tx("Remove entry")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          ))}
          <Button
            type="button"
            variant="secondary"
            className="h-12 w-full rounded-xl text-sm font-medium text-muted-foreground"
            onClick={addDictionaryEntry}
          >
            <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
            {tx("Add entry")}
          </Button>
        </div>
      </div>

      {summaryStyleLocked ? (
        <div className="mt-4 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
          {tx("Custom summarisation style needs")} {funPlanRequirementCopy}.
        </div>
      ) : null}

      {longerVoiceNotesLocked ? (
        <div className="mt-4 rounded-2xl bg-primary/5 px-4 py-3 text-sm text-primary">
          {tx("Longer voice notes need")} {proPlanRequirementCopy}.
        </div>
      ) : null}
    </section>
  );
}
